import { Typography } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { useUserDisplayNameQuery } from '@uplift/queries';
import { useL10n } from '@uplift/hooks';

const detectLastNameDuplicate = (preferredName: string, lastName: string) => {
  const trimmedLastName = lastName.trim();

  if (!preferredName || !trimmedLastName) return null;

  const escaped = trimmedLastName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const duplicateMatch = new RegExp(`\\b${escaped}\\b`, 'i').exec(preferredName);

  if (!duplicateMatch) return null;

  return {
    beforeDuplicate: preferredName.slice(0, duplicateMatch.index),
    duplicatedText: duplicateMatch[0],
    afterDuplicate: preferredName.slice(duplicateMatch.index + duplicateMatch[0].length),
  };
};

type PreferredNamePreviewProps = {
  preferredName: string;
};

export const PreferredNamePreview: React.VFC<PreferredNamePreviewProps> = ({ preferredName }) => {
  const l10n = useL10n();
  const me = useCurrentuser();
  const { data: nameDisplay } = useUserDisplayNameQuery(me?.id, { enabled: !!me });

  const trimmedPreferredName = preferredName.trim();
  const displayFirstName = trimmedPreferredName || me?.firstName;

  const prefix = nameDisplay?.prefix;
  const suffix = nameDisplay?.suffix;
  const lastName = me?.lastName;

  const duplicate = lastName ? detectLastNameDuplicate(trimmedPreferredName, lastName) : null;
  const hasDuplicateLastName = !!duplicate;
  const { beforeDuplicate = '', duplicatedText = '', afterDuplicate = '' } = duplicate ?? {};

  const namePart = (firstName: React.ReactNode) => (
    <>
      {prefix && <Typography component="span" fontWeight={500}>{prefix} </Typography>}
      {firstName}
      {lastName && <Typography component="span" fontWeight={500}> {lastName}</Typography>}
      {suffix && <Typography component="span" fontWeight={500}>, {suffix}</Typography>}
    </>
  );

  return (
    <>
      <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 1 }}>
        Clients will see:{' '}
        {hasDuplicateLastName ? namePart(
          <>
            <Typography component="span" fontWeight={500}>{beforeDuplicate}</Typography>
            <Typography component="span" fontWeight={500} color="error.main">{duplicatedText}</Typography>
            <Typography component="span" fontWeight={500}>{afterDuplicate}</Typography>
          </>
        ) : namePart(
          <Typography component="span" fontWeight={500}>{displayFirstName}</Typography>
        )}
      </Typography>
      {hasDuplicateLastName && (
        <Typography variant="caption" color="error.main" display="block">
          {l10n.providerSettings.preferredNameDuplicateLastName}
        </Typography>
      )}
    </>
  );
};
